import axios from "axios";
import { renderDicomFirstFrameThumb } from "@/lib/dicomFirstFrame";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const thumbCache = new Map();
const inflight = new Map();
const queue = [];
let activeJobs = 0;
const MAX_JOBS = 1;

let paused = false;
const pauseWaiters = [];

const fileServeUrl = (fileId) => (
  fileId === "sample" ? `${API}/dicom/sample` : `${API}/files/${fileId}/serve`
);

/** Pause background downloads while the active cine is fetching. */
export const setDicomPreviewPaused = (value) => {
  paused = Boolean(value);
  if (!paused) {
    while (pauseWaiters.length > 0) {
      pauseWaiters.shift()?.();
    }
    pumpQueue();
  }
};

const waitIfPaused = () => {
  if (!paused) return Promise.resolve();
  return new Promise((resolve) => {
    pauseWaiters.push(resolve);
  });
};

const pumpQueue = () => {
  if (paused) return;
  while (activeJobs < MAX_JOBS && queue.length > 0) {
    const job = queue.shift();
    if (!job) continue;
    activeJobs += 1;
    Promise.resolve()
      .then(job.run)
      .then(job.resolve, job.reject)
      .finally(() => {
        activeJobs = Math.max(0, activeJobs - 1);
        pumpQueue();
      });
  }
};

const enqueue = (run) => new Promise((resolve, reject) => {
  queue.push({ run, resolve, reject });
  pumpQueue();
});

export const captureDwvLayerThumb = (thumbW = 256, thumbH = 192) => {
  const root = document.getElementById("dwv-layerGroup");
  if (!root) return null;
  const src = [...root.querySelectorAll("canvas")]
    .filter((c) => c.width > 2 && c.height > 2)
    .sort((a, b) => (b.width * b.height) - (a.width * a.height))[0];
  if (!src) return null;

  const canvas = document.createElement("canvas");
  canvas.width = thumbW;
  canvas.height = thumbH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, thumbW, thumbH);
  const scale = Math.min(thumbW / src.width, thumbH / src.height);
  const dw = src.width * scale;
  const dh = src.height * scale;
  ctx.drawImage(src, (thumbW - dw) / 2, (thumbH - dh) / 2, dw, dh);
  try {
    return canvas.toDataURL("image/jpeg", 0.82);
  } catch (_) {
    return null;
  }
};

export const getCachedDicomFilePreviewThumb = (fileId) => {
  if (!fileId) return null;
  return thumbCache.get(fileId) || null;
};

export const rememberDicomFilePreviewThumb = (fileId, dataUrl) => {
  if (!fileId || !dataUrl) return;
  thumbCache.set(fileId, dataUrl);
};

/**
 * First-frame JPEG for a single-file DICOM, without DWV.
 */
export const getDicomFilePreviewThumb = (fileId) => {
  if (!fileId) return Promise.reject(new Error("missing file id"));
  const cached = getCachedDicomFilePreviewThumb(fileId);
  if (cached) return Promise.resolve(cached);
  if (inflight.has(fileId)) return inflight.get(fileId);

  const promise = enqueue(async () => {
    const hit = getCachedDicomFilePreviewThumb(fileId);
    if (hit) return hit;
    await waitIfPaused();
    const cachedAfterWait = getCachedDicomFilePreviewThumb(fileId);
    if (cachedAfterWait) return cachedAfterWait;

    const res = await axios.get(fileServeUrl(fileId), { responseType: "arraybuffer" });
    const dataUrl = await renderDicomFirstFrameThumb(res.data);
    rememberDicomFilePreviewThumb(fileId, dataUrl);
    return dataUrl;
  });

  inflight.set(fileId, promise);
  return promise.finally(() => {
    inflight.delete(fileId);
  });
};
